import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useVersionStore } from '@/stores/version-store';
import { isReloading, markReloading } from '@/lib/chunk-recovery';

/**
 * 새 버전 감지 이후의 화면 이동을 SPA 전환 대신 전체 로드로 바꾼다.
 * 옛 번들이 메모리에 남은 채 메뉴를 옮기면 이미 삭제된 옛 청크를 찾다 깨지기 때문이다.
 *
 * - 새 버전 감지 이후 첫 pathname 변경에만 동작한다. 쿼리·해시만 바뀌는 이동(필터 조작)은 제외.
 * - 감지 시점에 머무는 화면은 건드리지 않는다 (배너·자정 자동 리로드는 그대로 유지).
 * - 지연 import가 병행 실패해도 markReloading()으로 isReloading()이 true라
 *   청크 복구 래퍼가 추가 리로드를 하지 않는다.
 * - 전체 로드는 location.reload()다. 라우터가 이미 pushState로 주소를 바꿔 둔 상태라
 *   같은 주소 assign은 (a) hash가 있으면 fragment 이동이 되어 리로드가 안 되고
 *   (b) 일반 이동이라 HTTP 휴리스틱 캐시의 옛 index.html을 재사용한다. reload()는 문서를 재검증한다.
 * - BrowserRouter 안, Suspense 바깥에 둔다.
 *
 * 설계 문서: docs/update-chunk-recovery-plan.md
 */
export function NavigationReloadGate() {
  const { pathname } = useLocation();
  const hasNewVersion = useVersionStore((s) => s.hasNewVersion);
  const prevPath = useRef(pathname);

  useLayoutEffect(() => {
    if (prevPath.current === pathname) return; // 쿼리·해시만 바뀐 이동(필터 조작)은 제외
    prevPath.current = pathname;
    if (!hasNewVersion || isReloading()) return;
    markReloading();
    // 주소는 라우터가 이미 반영했다. reload()로 새 index.html을 재검증해 새 번들로 전체 로드한다.
    window.location.reload();
  }, [pathname, hasNewVersion]);

  return null;
}
